import dayjs from '../../utils/dayjs';
import { BlockStack, Box, DataTable, InlineStack, Link, Page, Text, useBreakpoints } from '@shopify/polaris';
import DateRangePicker from 'components/dateRangePicker';
import { Helmet } from 'react-helmet-async';

import { useCallback, useEffect, useState } from 'react';
import { useGetDriverStats, useGetFinalStaffRevenue, useGetJourneyRevenue } from 'queries/analytics.query';
import __helpers from 'helpers/index';

type StaffRevenue = {
  user_id: string;
  display_name: string;
  user_phonenumber: string;
  total_order: number;
  total_fare: number;
  total_passenger: number;
  total_good: number;
  rank: number;
  is_top: boolean;
};

export default function GeneralAnalytics() {
  const [defaultDate, setDefaultDate] = useState<any>({
    startDate: dayjs(dayjs().add(-7, 'day')).format('YYYY-MM-DD 00:00:00'),
    endDate: dayjs(new Date()).format('YYYY-MM-DD 23:59:59'),
  });

  /** Tài xế */
  const { data: driverStatData, isLoading: loadingDriverStat } = useGetDriverStats(defaultDate);
  const [driverTableData, setDriverTableData] = useState([]);

  useEffect(() => {
    if (!__helpers.isArray(driverStatData)) return;
    if (__helpers.isEmpty(driverStatData)) {
      setDriverTableData([]);
    } else {
      let totalResult = [];
      for (let ele of driverStatData) {
        totalResult.push([
          <Link
            onClick={() => (window.location.href = `/users/view/${ele.user_id}`)}
          >{`${ele.display_name} (${ele.user_phonenumber || 'no_phone'})`}</Link>,
          ele.total_trip_manifest,
          ele.total_trip_mainline,
          ele.total_trip_shuttle,
        ]);
      }
      setDriverTableData(totalResult);
    }
  }, [driverStatData]);

  /** Hành trình */

  const { data: journeyRevenueData, isLoading: loadingJourneyRevenueData } = useGetJourneyRevenue(defaultDate);
  const [journeyTableData, setJourneyTableData] = useState([]);
  useEffect(() => {
    if (!__helpers.isArray(journeyRevenueData)) return;
    if (__helpers.isEmpty(journeyRevenueData)) {
      setJourneyTableData([]);
    } else {
      let totalResult = [];
      for (let ele of journeyRevenueData) {
        totalResult.push([ele.journey_name, ele.order_count, ele.trip_count, __helpers.formatNumber(ele.revenue)]);
      }
      setJourneyTableData(totalResult);
    }
  }, [journeyRevenueData]);

  /** Nhân viên */
  const { data: dataStaffRevenue, isLoading: loadingDataStaffRevenue } = useGetFinalStaffRevenue(defaultDate);
  const [tableData, setTableData] = useState([]);
  const [totalOrder, setTotalOrder] = useState<number>(0);
  const [totalFare, setTotalFare] = useState<number>(0);
  const [totalPassenger, setTotalPassenger] = useState<number>(0);
  const [totalGood, setTotalGood] = useState<number>(0);

  useEffect(() => {
    if (!__helpers.isArray(dataStaffRevenue)) return;
    const staffRevenue = dataStaffRevenue as StaffRevenue[];
    if (__helpers.isEmpty(staffRevenue)) {
      setTotalOrder(0);
      setTotalFare(0);
      setTotalPassenger(0);
      setTotalGood(0);
      setTableData([]);
    } else {
      let totalResult = [];
      for (let ele of staffRevenue) {
        totalResult.push([
          <Link
            onClick={() => (window.location.href = `/users/view/${ele.user_id}`)}
          >{`${ele.display_name} (${ele.user_phonenumber || 'no_phone'})`}</Link>,
          __helpers.formatNumberWithCommas(ele.total_order),
          __helpers.formatNumberWithCommas(ele.total_fare),
          __helpers.formatNumberWithCommas(ele.total_good),
          __helpers.formatNumberWithCommas(ele.total_passenger),
        ]);
      }
      const totalOrder = staffRevenue.reduce((sum, item) => sum + item.total_order, 0);
      const totalFare = staffRevenue.reduce((sum, item) => sum + item.total_fare, 0);
      const totalPassenger = staffRevenue.reduce((sum, item) => sum + item.total_passenger, 0);
      const totalGood = staffRevenue.reduce((sum, item) => sum + item.total_good, 0);
      setTotalOrder(totalOrder);
      setTotalFare(totalFare);
      setTotalPassenger(totalPassenger);
      setTotalGood(totalGood);
      setTableData(totalResult);
    }
  }, [dataStaffRevenue]);

  const dateChangeCallback = useCallback((_e) => {
    let { since, until } = _e.period;
    let FNS = {
      startDate: '',
      endDate: '',
    };
    if (since) {
      FNS.startDate = new Date(dayjs(since).format('YYYY-MM-DD 00:00:00')).toISOString();
    }
    if (until) {
      FNS.endDate = new Date(dayjs(until).format('YYYY-MM-DD 23:59:59')).toISOString();
    }
    setDefaultDate(FNS);
  }, []);

  let { lgDown } = useBreakpoints();
  const fixedFirstColumns = lgDown ? 1 : 0;

  return (
    <>
      <Helmet>
        <title>Báo cáo tổng quát</title>
      </Helmet>
      <Page>
        <InlineStack align="space-between" blockAlign="center" gap="400">
          <Text as="h2" variant="headingLg">
            Báo cáo
          </Text>
          <DateRangePicker onChange={dateChangeCallback} Alignment="right" />
        </InlineStack>
        <br />
        <BlockStack gap="400">
          <Box>
            <Text as="p">Doanh thu bán vé</Text>
            <br />

            <DataTable
              hideScrollIndicator
              fixedFirstColumns={fixedFirstColumns}
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
              headings={['Nhân viên', 'Tổng đơn', 'Tổng tiền vé', 'Tổng hàng', 'Tổng khách']}
              rows={tableData}
              totals={[
                '',
                __helpers.formatNumberWithCommas(totalOrder),
                __helpers.formatNumberWithCommas(totalFare),
                __helpers.formatNumberWithCommas(totalGood),
                __helpers.formatNumberWithCommas(totalPassenger),
              ]}
              showTotalsInFooter
              hasZebraStripingOnData
              increasedTableDensity
            />
          </Box>
          <Text as="p">
            Doanh thu bán vé có thể xem và tải về <Link url="/analytics/employees">tại đây</Link>.
          </Text>
          <Box>
            <Text as="p">Tài xế</Text>
            <br />

            <DataTable
              hideScrollIndicator
              fixedFirstColumns={fixedFirstColumns}
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
              headings={['Nhân viên', 'Tổng chuyến', 'Chuyến chính', 'Chuyến trung chuyển']}
              rows={driverTableData}
              showTotalsInFooter
              hasZebraStripingOnData
              increasedTableDensity
            />
          </Box>

          <Box>
            <Text as="p">Doanh thu theo tuyến</Text>
            <br />

            <DataTable
              hideScrollIndicator
              fixedFirstColumns={fixedFirstColumns}
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
              headings={['Tuyến', 'Tổng đơn', 'Tổng chuyến', 'Doanh thu']}
              rows={journeyTableData}
              showTotalsInFooter
              hasZebraStripingOnData
              increasedTableDensity
            />
          </Box>
        </BlockStack>
      </Page>
    </>
  );
}

